// src/components/HR/components/Pages/Departments.js
import React, { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import Card from '../Card';
import Button from '../UI/Button';
import { hrApi } from '../../../api';
import { getOrgId } from '../../utils/auth';

const Departments = ({ userRole, canWrite: canWriteProp } = {}) => {
  const org_id = getOrgId();
  // State for data from API
  const [departmentsData, setDepartmentsData] = useState([]);
  const [loading, setLoading] = useState(false);

  // UI State
  const [view, setView] = useState('workers'); // 'workers' | 'users'
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [branchFilter, setBranchFilter] = useState('All');

  // --- API: Fetch Departments ---
  useEffect(() => {
    const fetchDepartments = async () => {
      setLoading(true);

      // Debugging: Check if API URL is correct in Console
      console.log('Fetching departments for view:', view);
      
      try {
        // Safety check: If imports failed, stop here
        if (!hrApi) {
          console.error("Error: hrApi is undefined. Please check your import path.");
          setLoading(false);
          return;
        }

        // Map 'users' UI state to 'staff' backend parameter
        const backendView = view === 'workers' ? 'workers' : 'staff';
        const url = `/orgs/${org_id}/hr/departments`;
        
        const response = await hrApi.get(url, {
          params: { view: backendView }
        });
        
        console.log('API Response Success:', response.data);

        // Ensure we are setting an array to state (handles cases where API might return { data: [...] })
        if (Array.isArray(response.data)) {
            setDepartmentsData(response.data);
        } else if (response.data && Array.isArray(response.data.data)) {
            // Fallback if backend wraps data in a 'data' property
            setDepartmentsData(response.data.data);
        } else {
            console.warn('Unexpected API response structure:', response.data);
            setDepartmentsData([]); 
        }

      } catch (error) {
        console.error("Error fetching departments:", error);
      } finally {
        setLoading(false);
      }
    };

    // Execute the function
    fetchDepartments();

  }, [org_id, view]); // Dependency: Re-run when 'view' or org changes

  // --- Logic: Filtering ---
  const filteredDepts = useMemo(() => {
    return departmentsData.filter(dept => {
      // Ensure properties exist to prevent crashes
      const name = dept.name || '';
      const type = dept.type || '';
      const branch = dept.branch || '';

      const matchesSearch = name.toLowerCase().includes(search.toLowerCase());
      const matchesType = typeFilter === 'All' || type === typeFilter;
      const matchesBranch = branchFilter === 'All' || branch === branchFilter;
      return matchesSearch && matchesType && matchesBranch;
    });
  }, [departmentsData, search, typeFilter, branchFilter]);

  // Get unique branches for the filter dropdown
  const uniqueBranches = useMemo(() => {
    if (!departmentsData) return [];
    return [...new Set(departmentsData.map(d => d.branch))].filter(b => b && b !== "—");
  }, [departmentsData]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Card>
        <div className="flex-between mb-4">
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button 
              variant={view === 'workers' ? 'primary' : 'secondary'} 
              onClick={() => setView('workers')}
            >
              Workers View
            </Button>
            <Button 
              variant={view === 'users' ? 'primary' : 'secondary'} 
              onClick={() => setView('users')}
            >
              Staff Users
            </Button>
          </div>
        </div>
        
        {/* Department Filters */}
        <div style={{ display: 'flex', gap: '8px', paddingBottom: '16px', borderBottom: '1px solid #eee', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
           <div style={{ position: 'relative', flex: '1 1 200px' }}>
              <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: '#999' }} />
              <input 
                type="text" 
                placeholder="Search departments..." 
                className="search-input" 
                style={{ paddingLeft: '32px', width: '100%' }} 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
              />
           </div>
           <select className="form-select" style={{ flex: '0 0 150px' }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="All">All Types</option>
              <option value="Inventory">Inventory</option>
              <option value="Production">Production</option>
              <option value="QC">QC</option>
              <option value="Sales">Sales</option>
              <option value="Accounts">Accounts</option>
              <option value="HR">HR</option>
              <option value="Admin">Admin</option>
           </select>
           <select className="form-select" style={{ flex: '0 0 150px' }} value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
              <option value="All">All Branches</option>
              {uniqueBranches.map(b => <option key={b} value={b}>{b}</option>)}
           </select>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: 'center' }}>Department Name</th>
                <th style={{ textAlign: 'center' }}>Type</th>
                <th style={{ textAlign: 'center' }}>Branch</th>
                <th style={{ textAlign: 'center' }}>{view === 'workers' ? 'Total Workers' : 'Total Staff'}</th>
                <th style={{ textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '24px' }}>Loading departments...</td>
                </tr>
              ) : filteredDepts.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '24px' }}>No departments found</td>
                </tr>
              ) : (
                filteredDepts.map(dept => (
                  <tr key={dept.id || Math.random()}>
                    <td style={{ fontWeight: 600, textAlign: 'center' }}>{dept.name}</td>
                    <td style={{ textAlign: 'center' }}>{dept.type}</td>
                    <td style={{ textAlign: 'center' }}>{dept.branch}</td>
                    <td style={{ textAlign: 'center' }}>{dept.total}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`status-badge ${dept.status === 'ACTIVE' ? 'status-active' : 'status-inactive'}`}>
                        {dept.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Departments;
